import { useEffect, useState } from "react";
import type { PageLayout } from "../../../../src/types";
import { useToast } from "../../hooks/use-toast";
import api from "../../lib/api";
import { Button } from "../ui/button";
import MobilePreview from "../virtual_pharmacy/MobilePreview";
import PageLayoutForm from "../virtual_pharmacy/PageLayoutForm";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
} from "../ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Eye,
  Pencil,
  Plus,
  Trash,
  Search,
  Filter,
  Grid3X3,
  List,
  Settings,
  Smartphone,
  Monitor,
  Tablet,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

function PageLayouts() {
  const [layouts, setLayouts] = useState<PageLayout[]>([]);
  const [filteredLayouts, setFilteredLayouts] = useState<PageLayout[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [layoutData, setLayoutData] = useState<PageLayout | null>(null);
  const [isCreated, setIsCreated] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [filterType, setFilterType] = useState<string>("all");
  const { toast } = useToast();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <toast is not a dependency>
  useEffect(() => {
    const loadLayouts = async () => {
      try {
        setIsLoading(true);
        const response = await api.fetchPageLayouts();
        if (response && "error" in response) {
          const errorMessage =
            typeof response.error === "object" &&
            response.error !== null &&
            "message" in response.error
              ? String(response.error.message)
              : String(response.error);
          toast({
            title: "Error",
            description: errorMessage || "An error occurred",
            variant: "destructive",
          });
        } else {
          console.log("Loaded layouts:", response); // Debug log
          setLayouts(response || []);
          setFilteredLayouts(response || []);
        }
      } catch (err: any) {
        toast({
          title: "Error",
          description: err.message,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadLayouts();
  }, [isCreated]);

  // Filter and search layouts
  useEffect(() => {
    let filtered = layouts;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(layout =>
        layout.page.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Apply type filter
    if (filterType !== "all") {
      filtered = filtered.filter(layout => {
        // Filter by page name pattern
        if (filterType === "home" && layout.page.toLowerCase().includes("home"))
          return true;
        if (
          filterType === "category" &&
          layout.page.toLowerCase().includes("categor")
        )
          return true;
        if (
          filterType === "product" &&
          layout.page.toLowerCase().includes("product")
        )
          return true;

        // Also check if layout contains specific component types
        const hasCarousel = layout.metadata?.some(s => s.type === "carousel");
        const hasCategories = layout.metadata?.some(
          s => s.type === "categories",
        );

        if (filterType === "home" && hasCarousel) return true;
        if (filterType === "category" && hasCategories) return true;

        return false;
      });
    }

    setFilteredLayouts(filtered);
  }, [layouts, searchTerm, filterType]);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setLayoutData(null);
  };
  const handleOpenPreview = (layoutData: PageLayout) => {
    setLayoutData(layoutData);
    setIsPreviewOpen(true);
  };
  const handleClosePreview = () => setIsPreviewOpen(false);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  const handleEditLayout = (layout: PageLayout) => {
    console.log("Editing layout:", layout); // Debug log
    setLayoutData(layout);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDeleteLayout = async (id: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this layout? This action cannot be undone.",
      )
    ) {
      try {
        const response = await api.deletePageLayout(id);
        if (response && "error" in response) {
          const errorMessage =
            typeof response.error === "object" &&
            response.error !== null &&
            "message" in response.error
              ? String(response.error.message)
              : String(response.error);
          toast({
            title: "Error",
            description: errorMessage || "An error occurred",
            variant: "destructive",
          });
        } else {
          setLayouts(layouts.filter(layout => layout.id !== id));
          toast({
            title: "Success",
            description: "Layout deleted successfully",
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete layout",
          variant: "destructive",
        });
      }
    }
  };

  const getLayoutStats = (layout: PageLayout) => {
    const sections = layout.metadata || [];
    const totalItems = sections.reduce((acc, section) => {
      if (section.type === "carousel" || section.type === "card") {
        return acc + (Array.isArray(section.data) ? section.data.length : 0);
      }
      if (section.type === "categories") {
        return acc + (section.data.items ? section.data.items.length : 0);
      }
      return acc + 1;
    }, 0);

    const componentTypes = new Set(sections.map(s => s.type));

    return {
      sections: sections.length,
      totalItems,
      hasImages: sections.some(section => {
        if (section.type === "banner") return Boolean(section.data.image);
        if (section.type === "carousel")
          return (
            Array.isArray(section.data) && section.data.some(item => item.image)
          );
        if (section.type === "card")
          return (
            Array.isArray(section.data) && section.data.some(item => item.icon)
          );
        if (section.type === "categories")
          return (
            section.data.items && section.data.items.some(item => item.image)
          );
        return false;
      }),
      componentTypes: Array.from(componentTypes),
    };
  };

  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {filteredLayouts.map(layout => {
        const stats = getLayoutStats(layout);
        return (
          <Card key={layout.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-lg font-semibold truncate">
                    {layout.page}
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    {new Date(layout.updated_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenPreview(layout)}
                  >
                    <Eye size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditLayout(layout)}
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteLayout(layout.id)}
                  >
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Settings size={14} />
                  <span>{stats.sections} sections</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Grid3X3 size={14} />
                  <span>{stats.totalItems} items</span>
                </div>
                {stats.hasImages && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Smartphone size={14} />
                    <span>Has images</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const renderListView = () => (
    <div className="w-full">
      <Table className="w-full bg-white dark:bg-gray-900 dark:text-white rounded-lg shadow-sm">
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Last Updated</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredLayouts.map(layout => {
            const stats = getLayoutStats(layout);
            return (
              <TableRow key={layout.id} className="border-b">
                <TableCell className="px-4 py-3">
                  <div>
                    <div className="font-medium">{layout.page}</div>
                    <div className="text-sm text-gray-500">
                      {stats.sections} sections, {stats.totalItems} items
                    </div>
                  </div>
                </TableCell>
                <TableCell className="px-4 py-3">
                  {new Date(layout.updated_at).toLocaleString()}
                </TableCell>
                <TableCell className="px-4 py-3">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => handleOpenPreview(layout)}
                      size="sm"
                    >
                      <Eye className="mr-1" size={16} />
                      Preview
                    </Button>
                    <Button
                      type="button"
                      onClick={() => handleEditLayout(layout)}
                      size="sm"
                      variant="outline"
                    >
                      <Pencil className="mr-1" size={16} />
                      Edit
                    </Button>
                    <Button
                      type="button"
                      onClick={() => handleDeleteLayout(layout.id)}
                      size="sm"
                      variant="destructive"
                    >
                      <Trash className="mr-1" size={16} />
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="flex flex-col items-center space-y-6">
      {/* Header */}
      <div className="flex w-full justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Page Layouts</h1>
          <p className="text-gray-600 mt-1">
            Manage and customize mobile app page layouts
          </p>
        </div>
        <Button onClick={handleOpenModal} size="lg">
          <Plus className="mr-2" size={20} />
          Add New Layout
        </Button>
      </div>

      {/* Filters and Search */}
      <div className="w-full flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex gap-4 items-center">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={16}
            />
            <Input
              placeholder="Search layouts..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Layouts</SelectItem>
              <SelectItem value="home">Home & Landing Pages</SelectItem>
              <SelectItem value="category">
                Category & Collection Pages
              </SelectItem>
              <SelectItem value="product">Product Pages</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("grid")}
          >
            <Grid3X3 size={16} />
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("list")}
          >
            <List size={16} />
          </Button>
        </div>
      </div>

      {/* Results Count */}
      <div className="w-full flex justify-between items-center">
        <p className="text-sm text-gray-600">
          {filteredLayouts.length} of {layouts.length} layouts
        </p>
      </div>

      {/* Content */}
      {filteredLayouts.length === 0 ? (
        <Card className="w-full">
          <CardContent className="py-12">
            <div className="text-center">
              <Smartphone size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No layouts found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || filterType !== "all"
                  ? "Try adjusting your search or filters"
                  : "Get started by creating your first page layout"}
              </p>
              {!searchTerm && filterType === "all" && (
                <Button onClick={handleOpenModal}>
                  <Plus className="mr-2" size={16} />
                  Create First Layout
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : viewMode === "grid" ? (
        renderGridView()
      ) : (
        renderListView()
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-6xl mx-auto flex flex-col max-h-[95vh]">
            <div className="sticky top-0 bg-white dark:bg-gray-900 px-6 py-4 border-b rounded-t-lg z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold dark:text-white">
                  {isEditing ? "Edit Layout" : "Add New Layout"}
                </h2>
                <Button
                  type="button"
                  onClick={handleCloseModal}
                  variant="ghost"
                  size="sm"
                >
                  ✕
                </Button>
              </div>
            </div>
            <div className="overflow-y-auto p-6">
              <PageLayoutForm
                layoutData={layoutData || undefined}
                onSuccess={() => {
                  setIsCreated(true);
                  handleCloseModal();
                  setIsEditing(false);
                  setLayoutData(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {isPreviewOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold dark:text-white">
                Mobile Preview
              </h2>
              <Button
                type="button"
                onClick={handleClosePreview}
                variant="ghost"
                size="sm"
              >
                ✕
              </Button>
            </div>
            <MobilePreview layoutData={layoutData} />
          </div>
        </div>
      )}
    </div>
  );
}

export default PageLayouts;
